// src/index.js
// Import process polyfill first to ensure it's available to all modules
import './utils/processPolyfill';

// Import all other modules after
import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import { UserProvider } from './context/UserContext';
import { ErrorBoundary } from './ErrorBoundary';
import HydrationErrorBoundary from './components/HydrationErrorBoundary';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import { initClientOptimizations } from './utils/criticalCss';
import { getEnv } from './utils/env';
import { initPerformanceMonitoring } from './utils/performance';
import { createRoot, hydrateRoot } from 'react-dom/client';

// Initialize performance monitoring only in browser
if (typeof window !== 'undefined') {
  initPerformanceMonitoring();
}

// Add browser features detection as data attributes on the HTML element
if (typeof window !== 'undefined' && typeof navigator !== 'undefined' && typeof document !== 'undefined') {
  if ('connection' in navigator) {
    document.documentElement.dataset.connectionType = navigator.connection.effectiveType;
    document.documentElement.dataset.saveData = navigator.connection.saveData;
  }

  document.documentElement.dataset.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// Initialize client optimizations as early as possible
if (typeof window !== 'undefined') {
  // Define deferred styles to load after initial render
  window.DEFERRED_STYLES = []; // Set to empty array
  
  // Start client-side optimization before React hydration
  initClientOptimizations(); // Uncommented
}

// Check if the page was prerendered
const isPrerendered = typeof document !== 'undefined' && 
  document.getElementById('root')?.hasAttribute('data-prerendered');

// Create the application jsx
const application = (
  <HydrationErrorBoundary>
    <ErrorBoundary>
      <BrowserRouter>
        <UserProvider>
          <App />
        </UserProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </HydrationErrorBoundary>
);

// Hydrate or render the app
const rootElement = document.getElementById('root');
if (isPrerendered) {
  hydrateRoot(rootElement, application);
} else {
  createRoot(rootElement).render(application);
}

// Add a data attribute to the HTML tag once the app has been hydrated
if (typeof document !== 'undefined') {
  document.documentElement.dataset.hydrated = 'true';
}

// Register the service worker for offline capabilities and performance benefits
serviceWorkerRegistration.register({
  onUpdate: registration => {
    // When a new version is available, show notification to the user
    const waitingServiceWorker = registration.waiting;
    if (waitingServiceWorker) {
      waitingServiceWorker.addEventListener("statechange", event => {
        if (event.target.state === "activated") {
          // Show a custom notification to the user
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('OriginSelect Updated', {
              body: 'New content is available! Reload to see the latest version.',
              icon: '/logo192.png'
            });
          }
          
          // Add update notification UI element
          const updateNotification = document.createElement('div');
          updateNotification.className = 'update-notification';
          updateNotification.innerHTML = `
            <p>New version available!</p>
            <button id="update-app-btn">Update Now</button>
          `;
          document.body.appendChild(updateNotification);
          
          document.getElementById('update-app-btn').addEventListener('click', () => {
            window.location.reload();
          });
        }
      });
      
      // Send message to the service worker to skip waiting
      waitingServiceWorker.postMessage({ type: 'SKIP_WAITING' });
    }
  }
});

// Measure and report performance metrics
reportWebVitals(metric => {
  // Send to analytics service in production
  if (getEnv('NODE_ENV') === 'production') {
    // Example: Send to Google Analytics
    // window.gtag('event', name, { value: delta, metric_id: id });
    
    // Log to console in development
    console.log(metric);
  }
});
